/**
 * Minimal accessible dropdown.
 *
 * Trigger button + absolutely positioned menu. Closes on:
 *   - selection
 *   - click outside
 *   - Escape
 *
 * Tab navigates within the menu when open. Returns focus to the
 * trigger on close. Sufficient for status/priority/assign in v1;
 * if we need richer behaviour (search, virtualised long lists) we'll
 * swap to Radix Popover.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/cn.js';

export interface DropdownItem<T> {
  value: T;
  label: ReactNode;
  description?: string;
  disabled?: boolean;
}

export interface DropdownProps<T extends string | null> {
  value: T;
  items: DropdownItem<T>[];
  onChange: (next: T) => void;
  /** Renders the trigger contents — typically the active pill. */
  renderTrigger: (current: DropdownItem<T> | undefined) => ReactNode;
  className?: string;
  /** Width of the menu — defaults to "match-trigger". */
  menuWidth?: number | 'match-trigger';
  ariaLabel: string;
}

export function Dropdown<T extends string | null>({
  value,
  items,
  onChange,
  renderTrigger,
  className,
  menuWidth = 'match-trigger',
  ariaLabel,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const current = items.find((i) => i.value === value);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        !menuRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const triggerWidth =
    menuWidth === 'match-trigger'
      ? triggerRef.current?.offsetWidth
      : menuWidth;

  return (
    <div className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-menu`}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'h-9 w-full bg-ink-800 border border-ink-600 rounded-md px-2 flex items-center justify-between gap-2 text-left focus-ring transition-colors',
          'hover:bg-ink-700'
        )}
      >
        <span className="flex items-center gap-2 min-w-0">
          {renderTrigger(current)}
        </span>
        <ChevronDown size={14} className="text-ink-300 shrink-0" />
      </button>

      {open && (
        <div
          ref={menuRef}
          id={`${id}-menu`}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 mt-1 z-50 bg-ink-800 border border-ink-600 rounded-md shadow-pop overflow-hidden dropdown-menu-in"
          style={triggerWidth ? { minWidth: triggerWidth } : undefined}
        >
          <ul className="max-h-72 overflow-auto py-1">
            {items.map((item) => {
              const selected = item.value === value;
              return (
                <li key={String(item.value)}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={item.disabled}
                    onClick={() => {
                      onChange(item.value);
                      setOpen(false);
                      triggerRef.current?.focus();
                    }}
                    className={cn(
                      'w-full text-left px-2.5 py-1.5 text-[13px] flex items-center gap-2 focus-ring disabled:opacity-50 disabled:pointer-events-none',
                      selected
                        ? 'bg-beacon-400/10 text-ink-100'
                        : 'text-ink-200 hover:bg-ink-700'
                    )}
                  >
                    <span className="flex-1 min-w-0">{item.label}</span>
                    {item.description && (
                      <span className="text-[11px] mono text-ink-400 shrink-0">
                        {item.description}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
